import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PhotoCredit from './PhotoCredit';
import Icon from './Icon';
import { recordRegularOpened } from '../../services/regularStripViews';
import { resizeForSurface } from '../../utils/imageSource';
import { formatCurrency } from '../../utils/currencyFormat';
import { formatTime, getDisplayLocale } from '../../utils/dateUtils';
import { patternText, nextOccurrenceText, occurrenceText } from '../../utils/activityScheduleText';

/**
 * A regular in a strip of event cards: a show, market or visit that happens at
 * a place on a schedule (docs/PLACE_ACTIVITIES.md). One card per regular, not
 * one per date: how often it runs above the name ("Every Wed–Sun"), then its
 * next time. Tapping opens its place at "Things to do here", which has the
 * full schedule, why go and booking.
 *
 * A confirmed regular's next time is one a source has confirmed; a usual
 * one's comes from its pattern, so it reads "Fri, Oct 23 · usually 9:00 AM"
 * (src/utils/regulars.js).
 *
 * The Link is a stretched overlay, as on the Highlights cards, so the photo
 * credit can be its own button.
 */
export default function RegularCard({ regular }) {
  const { t, i18n } = useTranslation('place');
  const locale = getDisplayLocale();
  const pattern = patternText(regular.schedule, t, locale);
  const next = regular.next && regular.next[0];
  const product = (regular.booking || [])[0];
  const price = regular.price_note
    || (product && product.from_price_cents != null
      ? t('activities.fromPrice', { price: formatCurrency(product.from_price_cents, product.currency, { fromCents: true }) })
      : null);
  const meta = [regular.family_fit ? t(`activities.familyFit.${regular.family_fit}`) : null, price].filter(Boolean);
  // exists() first: with returnEmptyString off, an empty defaultValue gives
  // back the key path, which would print as the chip.
  const kindKey = `activities.card.kind.${regular.kind}`;
  const kindLabel = i18n.exists(kindKey, { ns: 'place' }) ? t(kindKey) : '';
  const photoUrl = regular.photo ? resizeForSurface(regular.photo.url, 'medium') : null;

  return (
    <div className="relative h-full bg-surface border border-subtle rounded-card overflow-hidden hover:border-primary transition-colors">
      <Link
        to={`/place/${regular.attraction_id}#place-activities`}
        onClick={() => recordRegularOpened(regular.id)}
        aria-label={regular.name}
        className="absolute inset-0 z-0 rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
      />
      <div className="pointer-events-none">
        <div className="relative h-40 bg-surface-secondary">
          {photoUrl ? (
            <img src={photoUrl} alt={regular.name} loading="lazy" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon name="calendar" className="w-7 h-7 text-primary/60" />
            </div>
          )}
          {kindLabel && (
            <span className="absolute top-2 left-2 z-raised px-2 py-0.5 rounded-full bg-surface/90 text-xs font-medium text-content-primary">
              {kindLabel}
            </span>
          )}
          {photoUrl && (
            <PhotoCredit src={photoUrl} size="md" position="bottom" fallbackText={regular.photo.credit || undefined} />
          )}
        </div>
        <div className="p-3">
          {pattern && <p className="text-xs font-semibold text-coral-dark">{pattern}</p>}
          <h3 className="mt-0.5 text-sm font-semibold text-content-primary leading-snug line-clamp-2">{regular.name}</h3>
          {regular.place_name && (
            <p className="text-xs text-content-secondary line-clamp-1">{t('activities.card.at', { place: regular.place_name })}</p>
          )}
          {next && (
            <p className="mt-1.5 text-xs font-semibold text-content-primary tabular-nums">
              {!regular.usual ? nextOccurrenceText(next, locale)
                : next.start ? t('activities.card.usualNext', { date: occurrenceText(next, locale), time: formatTime(next.start) })
                  : t('activities.card.usualNextDay', { date: occurrenceText(next, locale) })}
            </p>
          )}
          {meta.length > 0 && <p className="mt-0.5 text-xs text-content-secondary">{meta.join(' · ')}</p>}
        </div>
      </div>
    </div>
  );
}
